import React, { useEffect } from "react";

import {
  Button,
  DocumentListItem,
  InfoPanel,
  Modal,
  Typography,
  UploadBox,
} from "@king-ict/eupisi-ui/components";
import { useTranslation } from "react-i18next";

import { getDocumentFromOrigin } from "../../../api/kindergartenApi";
import { RequestDocumentData } from "../../../models/Request/RequestDocumentData";

interface Props {
  open: boolean;
  allDocuments: RequestDocumentData[];
  onConfirm: (documents: RequestDocumentData[]) => any;
  onClose: (documents: RequestDocumentData[]) => any;
  onUpload: (files: File[]) => Promise<RequestDocumentData[]>;
  onRemove: (document: RequestDocumentData) => any;
  onDownload: (document: RequestDocumentData) => any;
  onExistingDocumentClick: (
    document: RequestDocumentData
  ) => Promise<RequestDocumentData>;
}

//TODO: Analyze whether it is better to change documents upload to onConfirm button click.
export default function DocumentsModal({
  open,
  allDocuments,
  onConfirm,
  onClose,
  onUpload,
  onRemove,
  onDownload,
  onExistingDocumentClick,
}: Props) {
  const [documents, setDocuments] = React.useState<RequestDocumentData[]>([]);
  const [
    showMaxDoucmentsError,
    setShowMaxDoucmentsError,
  ] = React.useState<boolean>(false);

  useEffect(() => {
    setShowMaxDoucmentsError(false);
  }, [open]);

  const handleFileChange = (files: File[]) => {
    if (files.length > 0) {
      if (documents.length + files.length > 10) {
        setShowMaxDoucmentsError(true);
      } else {
        setShowMaxDoucmentsError(false);
        onUpload(files).then((results) => {
          setDocuments((prev) => [...prev, ...results]);
        });
      }
    }
  };

  const handleDownloadFromOrigin = (document: RequestDocumentData) => {
    if (document.fileID) {
      getDocumentFromOrigin(document.origin, document.fileID);
    }
  };

  const resetForm = () => {
    onClose([...documents]);

    setDocuments([]);
  };

  const confirmForm = () => {
    onConfirm([...documents]);
    setDocuments([]);
  };

  const removeDocument = (document: RequestDocumentData) => {
    const newDocuments = documents.filter((d) => d.id && d.id !== document.id);

    setDocuments(newDocuments);

    onRemove(document);
  };

  const addExistingDocument = (document: RequestDocumentData) => {
    onExistingDocumentClick(document).then((result) => {
      setDocuments((prev) => [...prev, result]);
    });
  };

  const { t } = useTranslation();

  return (
    <Modal
      type={"confirm"}
      open={open}
      onClose={resetForm}
      onConfirm={confirmForm}
      confirmText={t("common.save")}
      cancelText={t("common.cancel")}
      variant="big"
    >
      <div>
        <Typography variant="baseBold">
          {t("documentUpload.modalTitle")}
        </Typography>
        <InfoPanel
          message={t("documentUpload.infoMessage")}
          color="success"
          fullWidth
        />
        <UploadBox
          filesAction={(file: any): void => handleFileChange(file)}
          info={t("documentUpload.uploadBoxInfo")}
          maxSize={10}
          types=".pdf, .jpg, .jpeg, .png, .tiff, .bmp"
        ></UploadBox>
      </div>
      {showMaxDoucmentsError ? (
        <InfoPanel
          message={t("documentUpload.maxDocumentErrorMessage")}
          color="danger"
          fullWidth
        />
      ) : (
        <></>
      )}
      <Typography variant="baseBold">
        {t("documentUpload.uploadedDocuments")}
      </Typography>

      {documents.map((document, index) => (
        <DocumentListItem
          key={`${index}_${document.fileName}`}
          document={document}
          onRemoveClick={removeDocument}
          onDocumentClick={onDownload}
        />
      ))}

      <br />
      <Typography variant="baseBold">
        {t("documentUpload.previouslyUploadedDocuments")}
      </Typography>
      {allDocuments.length > 0 && (
        <InfoPanel
          message={t("documentUpload.previouslyUploadedDocumentsInfo")}
          color="success"
          fullWidth
        />
      )}
      {allDocuments.map((document, index) => (
        <DocumentListItem
          key={`${index}_${document.fileName}`}
          document={document}
          onDocumentClick={handleDownloadFromOrigin}
          customAction={
            <Button
              size="sm"
              onClick={() => addExistingDocument(document)}
              outline
            >
              {t("documentUpload.attachPreviouslyUploadedDocument")}
            </Button>
          }
        />
      ))}
    </Modal>
  );
}
